import cx from 'clsx';
import React, { useState } from 'react';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as FilterIcon } from '@assets/svg/filter.svg';
import { ReactComponent as ListIcon } from '@assets/svg/ic-legendSwitch.svg';
import { ReactComponent as SearchIcon } from '@assets/svg/search.svg';

import ResetButton from '@components/common/ResetButton/ResetButton';
import FilterSearch from '@components/uikit/FilterSearch/FilterSearch';
import Select from '@components/uikit/Select/Select';
import Switch from '@components/uikit/Switch/Switch';

import { getAITypesList } from '@helpers/getAITypesList';
import { getAIUrgenciesList } from '@helpers/getAIUrgenciesList';

import useTranslation from '@hooks/useTranslation';

import { STATUS_LEGEND_TITLES } from '@pages/SensorsList/constants/statusLegendsTitles';

import { STATUSES_LEGEND } from '../../constants/statusesLegend';
import {
  selectSensorAiType,
  selectSensorAiUrgency,
  selectSensorSearch,
  selectSensorWithAi,
  useSensorStoreActions,
} from '../../store/useSensorStore';
import AdditionalSensorsFilters from '../AdditionalSensorsFilters/AdditionalSensorsFilters';
import style from './style.module.scss';

const ADDITIONAL_FILTERS_ID = 'additional-filters';
const FILTER_TOOLTIP = 'filter-tooltip';

const FilterPanel = () => {
  const { t } = useTranslation();

  const [showAdditionalFilter, setShowAdditionalFilter] = useState(false);

  const {
    setSensorAiType,
    setSensorAiUrgency,
    setSensorSearch,
    setSensorWithAi,
    setSensorFilterReset,
    setSensorLocationId,
  } = useSensorStoreActions();

  const sensorAiType = selectSensorAiType();
  const sensorAiUrgency = selectSensorAiUrgency();
  const sensorAiSearch = selectSensorSearch();
  const sensorWithAi = selectSensorWithAi();

  const [extraPanel, setExtraPanel] = useState(sensorAiSearch ? 'search' : 'legends');

  const typesList = getAITypesList(t);
  const urgenciesList = getAIUrgenciesList(t);

  const filterHasChanges = sensorAiType.value !== 'all' || sensorAiUrgency.value !== 'all';
  const showResetFilterBtn = filterHasChanges || sensorWithAi;

  const getChangedName = (item: string) => {
    if (item !== 'no ai') {
      return item;
    }

    if (sensorAiType === 'regular') return 'No Regular Action Item';
    if (sensorAiType === 'waitesWireless') return 'No Hardware Action Item';

    return 'no ai';
  };

  const handleChangeExtraPanel = (value: string) => {
    setExtraPanel(value);
    setSensorSearch('');
  };

  const handleSearch = (searchValue: string) => {
    setSensorSearch(searchValue);

    if (searchValue.length > 3) {
      setSensorLocationId(undefined);
    }
  };

  return (
    <div className={style.filterPanel}>
      <div className={style.filter}>
        <div className={style.sensorsOverviewFilter}>
          <div className={style.switchBlock}>
            <Switch
              id="allow-view-sensor"
              value={sensorWithAi}
              onChange={() => setSensorWithAi(!sensorWithAi)}
              label={t('Only Sensor with Action Item')}
              type="woHelperText"
            />
          </div>
          <div className={style.additionalShow}>
            <Select
              value={sensorAiType}
              onChange={(value: string) => setSensorAiType(value)}
              label={t('Action Items Type')}
              options={typesList}
              itemKeys={{ valueFieldKey: 'value', labelFieldKey: 'label' }}
              dataQa="ai-type-select"
              type="woHelperText"
            />
            <Select
              value={sensorAiUrgency}
              onChange={(value: string) => setSensorAiUrgency(value)}
              label={t('Urgency')}
              options={urgenciesList}
              itemKeys={{ valueFieldKey: 'value', labelFieldKey: 'label' }}
              dataQa="ai-urgency-select"
              type="woHelperText"
            />
          </div>
          <div
            className={style.additionalFilters}
            data-tooltip-id={ADDITIONAL_FILTERS_ID}
            onClick={() => setShowAdditionalFilter(true)}
          >
            <div
              className={cx(style.iconContainer, {
                [style.active]: showAdditionalFilter,
                [style.hasChanges]: filterHasChanges,
              })}
              data-tooltip-content={t('View Additional Filters')}
              data-tooltip-id={FILTER_TOOLTIP}
            >
              <FilterIcon className={style.threeDotsIcon} />
            </div>
          </div>
          <ResetButton showResetButton={showResetFilterBtn} onClick={setSensorFilterReset} />
        </div>
        <div className={style.switcherBlock}>
          <div
            className={cx(style.switchItem, { [style.switchLegendsActive]: extraPanel === 'legends' })}
            onClick={() => handleChangeExtraPanel('legends')}
            data-tooltip-content={t('View statuses legend')}
            data-tooltip-id={FILTER_TOOLTIP}
          >
            <ListIcon />
          </div>
          <div
            className={cx(style.switchItem, { [style.switchSearchActive]: extraPanel === 'search' })}
            onClick={() => handleChangeExtraPanel('search')}
            data-tooltip-content={t('Search')}
            data-tooltip-id={FILTER_TOOLTIP}
          >
            <SearchIcon />
          </div>
        </div>
      </div>
      {extraPanel === 'search' && (
        <div className={style.searchBlock}>
          <FilterSearch
            value={sensorAiSearch}
            variant={'header-search'}
            placeholder={t('Start to type...')}
            onChange={handleSearch}
          />
        </div>
      )}
      {extraPanel === 'legends' && (
        <div className={style.legendsBlock}>
          <span className={style.legendsLabel}>{t('Statuses legend')}</span>
          <div className={style.legendList}>
            {Object.keys(STATUSES_LEGEND).map((item: string) => (
              <div
                key={item}
                className={style.legendItem}
                data-tooltip-id="legends"
                data-tooltip-content={t(STATUS_LEGEND_TITLES[item as keyof typeof STATUS_LEGEND_TITLES])}
              >
                <div
                  className={cx(
                    style.legendItemIcon,
                    style[STATUSES_LEGEND[item as keyof typeof STATUSES_LEGEND]],
                  )}
                />
                <span className={style.legendItemName}>{t(getChangedName(item))}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <AdditionalSensorsFilters
        id={ADDITIONAL_FILTERS_ID}
        isOpen={showAdditionalFilter}
        setIsOpen={setShowAdditionalFilter}
      />
      <Tooltip id={FILTER_TOOLTIP} />
    </div>
  );
};

export default FilterPanel;
